import { h } from 'preact';
import useFormStore from '../../store/useFormStore';
import FranchiseInfoBlock from '../../components/blocks/FranchiseInfoBlock';
import PromotionsBlock from '../../components/promotions/PromotionsBlock';
import CartAccordionMobile from '../../components/cart/CartAccordionMobile';
import BackButton from '../../components/buttons/BackButton';
import NextButton from '../../components/buttons/NextButton';
import ProgressStepsArrow from '../../components/ProgressStepsArrow/ProgressStepsArrow';
import PrintButton from '../../components/buttons/PrintButton';
import ShortLeadInfoBlock from '../../components/blocks/ShortLeadInfoBlock';

/**
 * Reusable layout component for multi-step form screens
 *
 * @param {Object} props
 * @param {preact.ComponentChildren} props.children - Main content area
 * @param {preact.ComponentChildren} [props.leftSidebar] - Optional left sidebar content
 * @param {preact.ComponentChildren} [props.rightSidebar] - Optional right sidebar content
 * @param {boolean} [props.showProgress=true] - Whether to show the progress bar
 * @param {boolean} [props.showNavigation=true] - Whether to show navigation buttons
 * @param {Function} [props.onBack] - Back button handler
 * @param {Function} [props.onNext] - Next button handler
 * @param {boolean} [props.nextDisabled=false] - Whether the Next button is disabled
 * @param {string} [props.nextText='Next'] - Text for the Next button
 * @param {string} [props.backText='Back'] - Text for the Back button
 * @param {boolean} [props.showBackButton=true] - Whether to show the Back button
 * @param {string} [props.contentMaxWidth='max-w-7xl'] - Max width class for content area
 * @param {boolean} [props.showFranchiseInfoBlock=true] - Whether to show the showFranchiseInfoBlock component.
 * @param {boolean} [props.showPromotionsBlock=true] - Whether to show the PromotionsBlock component.
 * @param {string} [props.title=''] - Optional title for the step
 * @param {string} [props.subtitle=''] - Optional subtitle for the step
 * @param {preact.ComponentChildren} [props.headerAction] - Optional action element to display next to the title
 * @param {boolean} [props.showMobileNav=true] - Whether to show the mobile navigation at the bottom
 * @param {boolean} [props.showPrint=false] - Whether to show the print button
 */
export default function FormStepLayout({
  children,
  leftSidebar,
  rightSidebar,
  showProgress = true,
  showNavigation = true,
  onBack,
  onNext,
  nextDisabled = false,
  nextText = 'Next',
  backText = 'Back',
  showBackButton = true,
  showFranchiseInfoBlock = true,
  showPromotionsBlock = true,
  contentMaxWidth = 'max-w-full',
  title = '',
  subtitle = '',
  showMobileNav = true,
  showPrint = false,
  headerAction = null,
}) {
  const currentScreen = useFormStore((state) => state.currentScreen);
  const setCurrentScreen = useFormStore((state) => state.setCurrentScreen);
  const isShortLeadForm = useFormStore((state) => state.selectedEstimateType === 'on_site_estimate_short');

  // Calculate grid column classes based on sidebar presence
  // Mobile order: main (1), right (2), left (3)
  // Desktop order: left (1), main (2), right (3)
  const getGridClasses = () => {
    if (leftSidebar && rightSidebar) {
      return {
        left: 'col-span-12 xl:col-span-3 order-3 xl:order-1',
        main: 'col-span-12 xl:col-span-6 order-1 xl:order-2',
        right: 'col-span-12 xl:col-span-3 order-2 xl:order-3'
      };
    } else if (leftSidebar || rightSidebar) {
      return {
        left: leftSidebar ? 'col-span-12 xl:col-span-3 order-2 xl:order-1' : null,
        main: leftSidebar || rightSidebar ? 'col-span-12 xl:col-span-9 order-1' : 'col-span-12',
        right: rightSidebar ? 'col-span-12 xl:col-span-3 order-2 xl:order-2' : null
      };
    }
    return {
      left: null,
      main: 'col-span-12',
      right: null
    };
  };

  const gridClasses = getGridClasses();

  return (

    <div className="bg-gray-100 pb-6 px-4 mx-auto">
      {/* Progress Bar */}
      {showProgress && <ProgressStepsArrow />}

      {/* Main Content Area with Optional Sidebars */}
      <div className={`${contentMaxWidth} mx-auto grid grid-cols-12 gap-6 xl:items-stretch items-start`}>
        {/* Left Sidebar */}
        {leftSidebar && (
          <aside aria-label="Additional information and promotions" className={`${gridClasses.left} flex flex-col gap-3.5`}>
            {/* Sticky buttons container */}
            {currentScreen !== 'select-service' && !isShortLeadForm && (
              <div className="hidden xl:flex flex-col gap-2 sticky top-[200px] z-1 bg-gray-100">
                {showBackButton && onBack && (
                  <BackButton
                    text={backText}
                    onBack={onBack}
                    className="coit-button w-full md:w-auto text-center items-center justify-center"
                  />
                )}
                <button
                  onClick={() => setCurrentScreen('select-service')}
                  className="coit-button w-full"
                >
                  Add Services
                </button>
              </div>
            )}
            {showFranchiseInfoBlock && (
              <FranchiseInfoBlock />
            )}
            {showPromotionsBlock && !isShortLeadForm && (
              <PromotionsBlock />
            )}
            {/* Add extra content to the left sidebar */}
            {leftSidebar}
          </aside>
        )}

        {/* Main Content */}
        <div className={gridClasses.main}>
          <div className="order-form-header bg-white p-4 md:p-6 shadow-sm print:hidden">
            {/* Optional Title */}
            {(title || subtitle || headerAction) && (
              <div className="mb-6 border-b border-b-gray-300 pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    {title && (
                      <div className="text-3xl font-bold">{title}</div>
                    )}
                    {subtitle && (
                      <p className="mb-0" dangerouslySetInnerHTML={{ __html: subtitle }} />
                    )}
                  </div>
                  {headerAction && (
                    <div className="shrink-0">{headerAction}</div>
                  )}
                </div>
              </div>
            )}

            {/* Print Button */}
            {showPrint && (
              <div className="flex justify-end mb-4 print:hidden">
                <PrintButton />
              </div>
            )}

            {children}

            {/* Navigation Buttons - Hidden on mobile, shown on desktop */}
            {showNavigation && (
              <div className="hidden xl:flex justify-between items-center mt-8">
                <div>
                  {showBackButton && (
                    <BackButton text={backText} onBack={onBack} />
                  )}
                </div>

                <div>
                  {onNext && (
                    <NextButton text={nextText} onNext={onNext} disabled={nextDisabled} />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        {rightSidebar && (
          <aside aria-label="Order summary and actions" className={`${gridClasses.right} flex flex-col gap-3.5`}>
            {/* Sticky button container */}
            {onNext && (
              <div className="hidden xl:block sticky top-[200px] z-1 bg-gray-100">
                <NextButton text={nextText} onNext={onNext} disabled={nextDisabled} className="coit-button w-full" />
              </div>
            )}
            {/* Scrollable sidebar content */}
            {isShortLeadForm ? (
                <ShortLeadInfoBlock />
              ) : (
                rightSidebar
            )}
          </aside>
        )}
      </div>
      {/* Mobile sticky navigation */}
      {showMobileNav && (
        <div className="sticky bottom-0 left-0 right-0 mt-4 shadow-md xl:hidden z-10">
          <CartAccordionMobile isShortLeadForm={isShortLeadForm} />
          {showNavigation && (
            <div className="service-mobile-nav bg-white p-4">
              <div className="flex justify-between items-center flex-wrap gap-2">
                {showBackButton && (
                  <BackButton
                    text={backText}
                    onBack={onBack}
                    className="coit-button-sm w-full md:w-auto text-center items-center justify-center"
                  />
                )}

                {onNext && (
                  <NextButton
                    text={nextText}
                    onNext={onNext}
                    disabled={nextDisabled}
                    className="coit-button-sm w-full md:w-auto"
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
);
}
